import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { agentUsageContract } from "@common/contracts/agent-usage.contract";
import type {
    AgentUsageMonthlyResponse,
    AgentUsageQuery,
    AgentUsageResponse,
} from "@common/schemas/domains/agentRuns/agent-usage.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";

const agentUsageClient = getBackendAPIClient(agentUsageContract);

export const agentUsageQueryKeys = {
    all: () => ["agent-usage"] as const,
    usage: (organizationId: string, query: AgentUsageQuery) =>
        [...agentUsageQueryKeys.all(), "usage", organizationId, query] as const,
    monthly: (organizationId: string, query: AgentUsageQuery) =>
        [
            ...agentUsageQueryKeys.all(),
            "monthly",
            organizationId,
            query,
        ] as const,
};

interface OrganizationAgentUsageParams {
    organizationId: string;
    query: AgentUsageQuery;
}

/**
 * The server's 404 (no visible client has agent runs on) resolves to `null`:
 * organization pages have no workspace in the LaunchDarkly context, so the
 * server is the only gate.
 */
const nullOn404 = async <T>(load: () => Promise<T>): Promise<T | null> => {
    try {
        return await load();
    } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

export function useOrganizationAgentUsage({
    organizationId,
    query,
}: OrganizationAgentUsageParams) {
    return useQuery({
        queryKey: agentUsageQueryKeys.usage(organizationId, query),
        enabled: !!organizationId,
        placeholderData: keepPreviousData,
        queryFn: (): Promise<AgentUsageResponse | null> =>
            nullOn404(async () => {
                const result = await agentUsageClient.getOrganizationAgentUsage(
                    {
                        params: { organizationId },
                        query,
                        extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                    },
                );
                return result.body;
            }),
    });
}

export function useOrganizationAgentUsageMonthly({
    organizationId,
    query,
}: OrganizationAgentUsageParams) {
    return useQuery({
        queryKey: agentUsageQueryKeys.monthly(organizationId, query),
        enabled: !!organizationId,
        placeholderData: keepPreviousData,
        queryFn: (): Promise<AgentUsageMonthlyResponse | null> =>
            nullOn404(async () => {
                const result =
                    await agentUsageClient.getOrganizationAgentUsageMonthly({
                        params: { organizationId },
                        query,
                        extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                    });
                return result.body;
            }),
    });
}
